import { clearToken, readToken } from './token'
import type {
  ConnectorInfo,
  ExternalUnit,
  Integration,
  IntegrationSummary,
  MappingInput,
  MappingResult,
  ParkDetail,
  ParkInput,
  ParkPatch,
  ParkSummary,
  SettingValue,
  SyncResult,
  AdminUnit,
  UnitMapping,
  UnitPatch,
  PlanUploadResult,
} from './types'

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

export class ApiError extends Error {
  readonly status: number
  /** Only in dev: exception class and stack detail. Handy to show, never to depend on. */
  readonly detail: string | null

  constructor(message: string, status: number, detail: string | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/** Turns whatever went wrong into one line of Dutch for the UI. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Onbekende fout'
}

/** The extra technical text a dev API sends along, for a collapsed block under the message. */
export function errorDetail(error: unknown): string | null {
  return error instanceof ApiError ? error.detail : null
}

function parse(text: string): unknown {
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

/** Everything under /admin answers with {error: "<Dutch sentence>"}, also on 400, 401 and 404. */
function errorField(data: unknown): string | null {
  if (data !== null && typeof data === 'object' && 'error' in data) {
    const value = (data as { error: unknown }).error
    if (typeof value === 'string' && value.trim() !== '') return value
  }
  return null
}

/** Dev-only extras; production leaves these out, so nothing may depend on them. */
function detailField(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  const parts: string[] = []
  if (typeof record.exception === 'string') parts.push(record.exception)
  if (typeof record.detail === 'string') parts.push(record.detail)
  return parts.length > 0 ? parts.join('\n') : null
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = readToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers['X-Admin-Token'] = token
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError('Geen verbinding met de API', 0)
  }

  if (res.status === 401) {
    // One shared token: a 401 can only mean the token is wrong, so throw it away and ask again.
    clearToken('unauthorized')
    throw new ApiError('Token klopt niet', 401)
  }

  const data = parse(await res.text())
  if (!res.ok) {
    throw new ApiError(errorField(data) ?? `Fout ${res.status} ${res.statusText}`.trim(), res.status, detailField(data))
  }
  const failed = errorField(data)
  if (failed !== null) throw new ApiError(failed, res.status, detailField(data))
  return data as T
}

const parkPath = (slug: string) => `/admin/api/parks/${encodeURIComponent(slug)}`
const integrationPath = (id: string) => `/admin/api/integrations/${encodeURIComponent(id)}`

/**
 * Each context serves its own admin data: the park comes from ParkMap, its booking systems from
 * Booking. The screens want them together, so they are merged here rather than in every component.
 */
export const fetchParks = async (): Promise<ParkSummary[]> => {
  const [parks, integrations] = await Promise.all([
    request<Omit<ParkSummary, 'integrations'>[]>('GET', '/admin/api/parks'),
    request<(IntegrationSummary & { parkSlug: string })[]>('GET', '/admin/api/integrations'),
  ])

  return parks.map((park) => ({
    ...park,
    integrations: integrations.filter((i) => i.parkSlug === park.slug),
  }))
}

export const fetchPark = async (slug: string): Promise<ParkDetail> => {
  const [park, booking] = await Promise.all([
    request<Omit<ParkDetail, 'integrations'>>('GET', parkPath(slug)),
    request<{ integrations: Integration[]; mappingsByUnitId: Record<string, Record<string, UnitMapping>> }>(
      'GET',
      `${parkPath(slug)}/integrations`,
    ),
  ])

  return {
    ...park,
    integrations: booking.integrations,
    units: park.units.map((unit) => ({ ...unit, mappings: booking.mappingsByUnitId[unit.id] ?? {} })),
  }
}

export const createPark = async (park: ParkInput): Promise<ParkSummary> => ({
  ...(await request<Omit<ParkSummary, 'integrations'>>('POST', '/admin/api/parks', park)),
  integrations: [],
})

export const patchPark = (slug: string, patch: ParkPatch) => request<unknown>('PATCH', parkPath(slug), patch)

/**
 * De plattegrond gaat als multipart de deur uit, dus buiten request() om: die zet een JSON-content-type
 * en dan komt het bestand er aan de andere kant niet uit.
 */
export async function uploadPlan(slug: string, file: File): Promise<PlanUploadResult> {
  const token = readToken()
  const form = new FormData()
  form.append('file', file)

  let res: Response
  try {
    res = await fetch(`${API_BASE}${parkPath(slug)}/plan`, {
      method: 'POST',
      headers: token ? { Accept: 'application/json', 'X-Admin-Token': token } : { Accept: 'application/json' },
      body: form,
    })
  } catch {
    throw new ApiError('Geen verbinding met de API', 0)
  }
  if (res.status === 401) {
    clearToken('unauthorized')
    throw new ApiError('Token klopt niet', 401)
  }
  const data = parse(await res.text())
  if (!res.ok) throw new ApiError(errorField(data) ?? `Fout ${res.status}`.trim(), res.status, detailField(data))

  return data as PlanUploadResult
}

export const patchUnit = (id: string, patch: UnitPatch) =>
  request<AdminUnit>('PATCH', `/admin/api/units/${encodeURIComponent(id)}`, patch)

export const fetchConnectors = () => request<ConnectorInfo[]>('GET', '/admin/api/connectors')

export const addIntegration = (slug: string, connector: string) =>
  request<Integration>('POST', `${parkPath(slug)}/integrations`, { connector })

export const patchIntegration = (
  id: string,
  patch: { settings?: Record<string, SettingValue>; clearSettings?: string[]; enabled?: boolean },
) =>
  request<Integration>('PATCH', integrationPath(id), patch)

export const deleteIntegration = (id: string) => request<unknown>('DELETE', integrationPath(id))

export const fetchExternalUnits = (id: string) =>
  request<{ units: ExternalUnit[] }>('GET', `${integrationPath(id)}/external-units`)

export const putMappings = (id: string, mappings: MappingInput[]) =>
  request<MappingResult>('PUT', `${integrationPath(id)}/mappings`, { mappings })

export const syncIntegration = (id: string, days?: number) =>
  request<SyncResult>('POST', `${integrationPath(id)}/sync`, days === undefined ? {} : { days })
